import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { DataAccessServiceInterface } from '@central-products/interface-data-access-service';
import { loadMicroApp } from '@central-products/script-module-loader';
import { Overlay } from '@sitebase/ui-kit';
import { microAppVisibleCSSAppend, microAppVisibleCSSRemove } from './utils/deviceUtils';
import { Environment, Jurisdiction, OnActionRequested, FlagType } from './types';

export interface AccountFlagsContainerProps {
  environment: Environment;
  jurisdiction: Jurisdiction;
  locale: string;
  dataAccessService: DataAccessServiceInterface;
  flag: {
    name: string;
    type: string;
    token: string | null;
    settings: {
      onSuccessCallbackTimeout: number;
      onQuitCallbackTimeout: number;
    };
    // tslint:disable-next-line:no-any
    options?: Record<string, any>;
  };
  onClose: () => void;
  onSuccess: () => void;
  onQuit: () => void;
  onLoginRequested: () => void;
  onActionRequested: OnActionRequested;
  executeLogin: (token?: string) => Promise<void>;
}

interface AccountFlagsContainerState {
  isLoading: boolean;
}

export class AccountFlagsContainer extends React.Component<AccountFlagsContainerProps, AccountFlagsContainerState> {
  public readonly state: AccountFlagsContainerState = {
    isLoading: true,
  };
  private rootNode: HTMLElement | null = null;
  private actionSuccessTimer: number | undefined;
  private actionQuitTimer: number | undefined;

  public async componentDidMount() {
    const {
      environment,
      jurisdiction,
      locale,
      dataAccessService,
      flag,
      onLoginRequested,
      onActionRequested,
    } = this.props;

    window.performance?.mark?.('cp:account-flags:load-bundle');
    microAppVisibleCSSAppend();

    const envPostfix = environment === Environment.PROD ? '' : `-${environment}`;
    const flapiUrl = `https://flapi-cs.williamhill${envPostfix}.${jurisdiction}`;

    try {
      await loadMicroApp({ name: flag.name, environment, jurisdiction, dependencies: { React, ReactDOM } });
      this.setState({ isLoading: false });
      window.WH['cp-apps'][flag.name].open(
        {
          environment,
          jurisdiction,
          locale,
          dataAccessService,
          flapiUrl,
          options: flag.options,
          token: flag.token,
          // TODO: to be removed in next major
          ...flag.options,
          onClose: this.onFlagClose,
          onSuccess: this.onFlagSuccess,
          onQuit: this.onFlagQuit,
          onLoginRequested,
          onActionRequested,
          // TODO: to be removed in next major
          openLogin: onLoginRequested,
        },
        this.rootNode as HTMLElement
      );
    } catch (e) {}
  }

  public componentWillUnmount() {
    microAppVisibleCSSRemove();
    window.WH['cp-apps'][this.props.flag.name].close();
    window.clearTimeout(this.actionSuccessTimer);
    window.clearTimeout(this.actionQuitTimer);
  }

  public render() {
    return (
      <Overlay className="cp-account-flags-overlay">
        {this.state.isLoading ? <div className="cp-account-flags-overlay__spinner" /> : null}
        <div className="cp-account-flags-overlay__container" ref={this.setRootNodeRef} />
      </Overlay>
    );
  }

  public onFlagClose = () => {
    this.props.onClose();
  };

  public onFlagSuccess = (token?: string) => {
    this.actionSuccessTimer = window.setTimeout(() => {
      if (this.props.flag.type === FlagType.HARD) {
        this.props.executeLogin(token);
      }

      this.props.onSuccess();
    }, this.props.flag.settings.onSuccessCallbackTimeout);
  };

  public onFlagQuit = () => {
    this.actionQuitTimer = window.setTimeout(this.props.onQuit, this.props.flag.settings.onQuitCallbackTimeout);
  };

  private setRootNodeRef = (node: HTMLElement | null) => {
    this.rootNode = node;
  };
}
